import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Calendar, MapPin, Users, Plus, Search, X, Clock, User, Check, Filter, Lock, Globe, UserPlus, Pencil, Save, History } from 'lucide-react';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import CustomDropdown from '../ui/CustomDropdown';
import PageHeader from '../ui/PageHeader';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTranslation, useLanguage } from '../../context/LanguageContext';
import AddressAutocomplete from '../AddressAutocomplete';
import {
    getEvents,
    createEvent,
    updateEvent,
    joinEvent,
    leaveEvent,
    getEventParticipants,
    addEventParticipant,
    addEventGroup,
    EUROPEAN_COUNTRIES
} from '../../services/eventsService';
import { getVisibleUserContacts } from '../../services/userContactsService';
import { getEventTypes, getEventTypesGrouped, getCategoryTranslation, getCategoryInfo, CATEGORY_DATA } from '../../services/eventsTypesService';

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon for events
const eventIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-violet.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
});

/**
 * Events Module
 * List and map of public events from all members
 */
const Events = () => {
    const { t } = useTranslation();
    const { language } = useLanguage();

    // Current user
    const currentUserId = parseInt(localStorage.getItem('userId') || '0', 10);

    // State
    const [publicEvents, setPublicEvents] = useState([]);
    const [pastEvents, setPastEvents] = useState([]);
    const [invitedEvents, setInvitedEvents] = useState([]);
    const [myEvents, setMyEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [searchKeyword, setSearchKeyword] = useState('');
    const [selectedFilterCategory, setSelectedFilterCategory] = useState(null); // Category filter (like Endroits)
    const [selectedEventTypes, setSelectedEventTypes] = useState([]); // Types within category (excluded ones)
    const [eventTypes, setEventTypes] = useState([]); // All event types flat list
    const [categories, setCategories] = useState([]); // List of categories
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showParticipants, setShowParticipants] = useState(false);
    const [participants, setParticipants] = useState([]);
    const [editingEvent, setEditingEvent] = useState(null); // Event being edited

    // Map center (Europe)
    const mapCenter = [50.8503, 4.3517]; // Brussels as default center

    // Load categories and event types on mount
    useEffect(() => {
        loadCategoriesAndTypes();
    }, [language]);

    const loadCategoriesAndTypes = async () => {
        try {
            // Load flat list of event types with all properties
            const typesResponse = await getEventTypes({ lang: language });
            // Handle both array response and {data: [...]} or {DATA: [...]} response (ColdFusion returns uppercase)
            const typesData = Array.isArray(typesResponse) ? typesResponse : (typesResponse?.data || typesResponse?.DATA || []);
            console.log('Loaded event types:', typesData?.length, typesData);
            if (Array.isArray(typesData) && typesData.length > 0) {
                setEventTypes(typesData);
                // Extract unique categories
                const uniqueCategories = [...new Set(typesData.map(t => t.category))].filter(Boolean);
                console.log('Unique categories:', uniqueCategories);
                setCategories(uniqueCategories);
            } else {
                // Fallback to static categories if API returns empty
                console.log('Using fallback categories');
                setCategories(Object.keys(CATEGORY_DATA));
            }
        } catch (error) {
            console.error('Error loading categories:', error);
            // Fallback to static categories on error
            setCategories(Object.keys(CATEGORY_DATA));
        }
    };

    // Get types for a specific category
    const getTypesForCategory = (category) => {
        return eventTypes.filter(t => t.category === category);
    };

    // Select a category filter
    const handleSelectFilterCategory = (category) => {
        setSelectedFilterCategory(category);
        setSelectedEventTypes([]); // Reset type exclusions
    };

    // Toggle type exclusion (deselect/reselect)
    const handleToggleTypeFilter = (type) => {
        const existing = selectedEventTypes.find(t => t.id === type.id);
        if (existing) {
            setSelectedEventTypes(selectedEventTypes.filter(t => t.id !== type.id));
        } else {
            setSelectedEventTypes([...selectedEventTypes, { ...type, excluded: true }]);
        }
    };

    // Exclude all types in category
    const handleExcludeAllTypes = () => {
        const categoryTypes = getTypesForCategory(selectedFilterCategory);
        setSelectedEventTypes(categoryTypes.map(t => ({ ...t, excluded: true })));
    };

    // Include all types in category
    const handleIncludeAllTypes = () => {
        setSelectedEventTypes([]);
    };

    // Load all event categories
    useEffect(() => {
        loadAllEvents();
    }, [selectedCountry, searchKeyword, selectedFilterCategory, selectedEventTypes, currentUserId]);

    const loadAllEvents = async () => {
        setLoading(true);
        const today = new Date().toISOString().split('T')[0];

        // Build common filter options
        const baseFilters = {
            countryCode: selectedCountry,
            keyword: searchKeyword,
            viewerId: currentUserId,
            limit: 100
        };
        // If category selected, filter by active types in that category
        if (selectedFilterCategory) {
            const categoryTypes = getTypesForCategory(selectedFilterCategory);
            const activeTypes = categoryTypes.filter(ct => !selectedEventTypes.find(st => st.id === ct.id && st.excluded));
            // Pass the active type codes to filter
            if (activeTypes.length > 0) {
                baseFilters.typeCodes = activeTypes.map(t => t.code).join(',');
            }
            baseFilters.category = selectedFilterCategory;
        }

        // Load all four categories in parallel
        const [publicResult, pastResult, invitedResult, myResult] = await Promise.all([
            // Public events (upcoming)
            getEvents({
                ...baseFilters,
                startDate: today,
                onlyPublic: true
            }),
            // Past events (all past public events)
            getEvents({
                ...baseFilters,
                endDate: today,
                onlyPublic: true
            }),
            // Invited events (private events where I'm invited but not creator)
            getEvents({
                ...baseFilters,
                startDate: today,
                onlyInvited: true
            }),
            // My events (where I'm creator or participant - past and future)
            getEvents({
                ...baseFilters,
                onlyMyEvents: true
            })
        ]);

        setPublicEvents(publicResult.events);
        setPastEvents(pastResult.events);
        setInvitedEvents(invitedResult.events);
        setMyEvents(myResult.events);
        setLoading(false);
    };

    // Clear all filters
    const clearAllFilters = () => {
        setSelectedCountry('');
        setSelectedFilterCategory(null);
        setSelectedEventTypes([]);
        setSearchKeyword('');
    };

    // Clear category filter only
    const handleClearCategoryFilter = () => {
        setSelectedFilterCategory(null);
        setSelectedEventTypes([]);
    };

    // Get all events for map display
    const getAllEventsForMap = () => {
        const allEvents = [...myEvents, ...invitedEvents, ...publicEvents];
        // Remove duplicates by id
        const uniqueEvents = allEvents.filter((event, index, self) =>
            index === self.findIndex(e => e.id === event.id)
        );
        return uniqueEvents;
    };

    const handleJoinEvent = async (event) => {
        const result = await joinEvent(event.id, currentUserId);
        if (result.success) {
            loadAllEvents();
        }
    };

    const handleLeaveEvent = async (event) => {
        const result = await leaveEvent(event.id, currentUserId);
        if (result.success) {
            loadAllEvents();
        }
    };

    const handleShowParticipants = async (event) => {
        setSelectedEvent(event);
        const participantsList = await getEventParticipants(event.id);
        setParticipants(participantsList);
        setShowParticipants(true);
    };

    const isCreator = (event) => event.creatorId === currentUserId;

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
            weekday: 'short',
            day: 'numeric',
            month: 'short'
        });
    };

    // Render a single event card (compact version for columns)
    const renderEventCard = (event) => (
        <div
            key={event.id}
            className={`bg-white rounded-lg p-3 shadow-sm border transition-shadow hover:shadow-md cursor-pointer ${
                !event.isPublic ? 'border-orange-200' : 'border-gray-100'
            }`}
            onClick={() => setSelectedEvent(event)}
        >
            <div className="flex justify-between items-start mb-1">
                <h4 className="font-medium text-gray-800 text-sm line-clamp-1">{event.title}</h4>
                {!event.isPublic && <Lock className="w-3 h-3 text-orange-500 flex-shrink-0" />}
            </div>
            <div className="text-xs text-gray-500 space-y-0.5">
                <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-cabaza-purple" />
                    <span>{formatDate(event.eventDate)}</span>
                    {event.startTime && <span className="text-gray-400">• {event.startTime}</span>}
                </div>
                <div className="flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-cabaza-purple" />
                    <span className="line-clamp-1">{event.placeName}</span>
                </div>
            </div>
            <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-400">
                    {event.participantCount} <Users className="w-3 h-3 inline" />
                </span>
                {isCreator(event) && (
                    <span className="text-xs bg-cabaza-purple/10 text-cabaza-purple px-1.5 py-0.5 rounded">
                        {language === 'fr' ? 'Org.' : 'Org.'}
                    </span>
                )}
            </div>
        </div>
    );

    return (
        <div className="h-full flex flex-col">
            {/* Header */}
            <PageHeader
                moduleCode="EVENTS"
                title={t.events?.title || (language === 'fr' ? 'Événements' : 'Events')}
                subtitle={t.events?.subtitle || (language === 'fr' ? 'Découvrez et créez des événements' : 'Discover and create events')}
            >
                <Button onClick={() => setShowCreateModal(true)} icon={Plus}>
                    {language === 'fr' ? 'Créer un événement' : 'Create Event'}
                </Button>
            </PageHeader>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
                {/* Country filter */}
                <div className="relative flex-1">
                    <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10 pointer-events-none" />
                    <CustomDropdown
                        value={selectedCountry}
                        onChange={(val) => setSelectedCountry(val)}
                        options={[
                            { value: '', label: language === 'fr' ? 'Tous les pays' : 'All countries' },
                            ...EUROPEAN_COUNTRIES.map(country => ({
                                value: country.code,
                                label: language === 'fr' ? country.name : country.nameEn
                            }))
                        ]}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg hover:border-cabaza-purple focus:ring-2 focus:ring-cabaza-purple"
                    />
                </div>

                {/* Category filter */}
                <div className="relative flex-1">
                    <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10 pointer-events-none" />
                    <CustomDropdown
                        value={selectedFilterCategory || ''}
                        onChange={(val) => handleSelectFilterCategory(val || null)}
                        options={[
                            { value: '', label: language === 'fr' ? 'Toutes les catégories' : 'All categories' },
                            ...categories.map(category => ({
                                value: category,
                                label: getCategoryTranslation(category, language)
                            }))
                        ]}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg hover:border-cabaza-purple focus:ring-2 focus:ring-cabaza-purple"
                    />
                </div>

                {/* Search */}
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder={language === 'fr' ? 'Rechercher par nom, lieu...' : 'Search by name, place...'}
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                    />
                </div>
            </div>

            {/* Selected category tag + types (like Endroits) */}
            {selectedFilterCategory && (
                <div className="space-y-3 mb-4">
                    {/* Category tag with actions */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <span
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-purple-100 text-purple-700"
                        >
                            {getCategoryTranslation(selectedFilterCategory, language)}
                            <button
                                onClick={handleClearCategoryFilter}
                                className="hover:bg-purple-200 rounded-full p-0.5"
                                title={language === 'fr' ? 'Retirer la catégorie' : 'Remove category'}
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </span>
                        {/* Toggle all types buttons */}
                        <button
                            onClick={handleExcludeAllTypes}
                            className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                            title={language === 'fr' ? 'Tout désélectionner' : 'Deselect all'}
                        >
                            {language === 'fr' ? 'Tout désélectionner' : 'Deselect all'}
                        </button>
                        <button
                            onClick={handleIncludeAllTypes}
                            className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                            title={language === 'fr' ? 'Tout sélectionner' : 'Select all'}
                        >
                            {language === 'fr' ? 'Tout sélectionner' : 'Select all'}
                        </button>
                    </div>

                    {/* Types within category - clickable to exclude/include */}
                    <div className="flex flex-wrap gap-2">
                        {getTypesForCategory(selectedFilterCategory).map(type => {
                            const isExcluded = selectedEventTypes.find(t => t.id === type.id && t.excluded);
                            return (
                                <button
                                    key={type.id}
                                    onClick={() => handleToggleTypeFilter(type)}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                                        isExcluded
                                            ? 'bg-gray-100 text-gray-400 line-through'
                                            : 'text-white'
                                    }`}
                                    style={!isExcluded ? { backgroundColor: type.color || '#6366f1' } : {}}
                                >
                                    {type.emoji && <span>{type.emoji}</span>}
                                    {type.name}
                                    {isExcluded && <X className="w-3.5 h-3.5 ml-1" />}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Map - Full width with reduced z-index */}
            <div className="h-[250px] rounded-xl overflow-hidden shadow-md mb-4 relative z-0">
                <MapContainer
                    center={mapCenter}
                    zoom={5}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={true}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {getAllEventsForMap().filter(e => e.latitude && e.longitude).map(event => (
                        <Marker
                            key={event.id}
                            position={[event.latitude, event.longitude]}
                            icon={eventIcon}
                        >
                            <Popup>
                                <div className="min-w-[200px]">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h3 className="font-bold text-gray-800">{event.title}</h3>
                                        {!event.isPublic && <Lock className="w-3 h-3 text-orange-500" />}
                                    </div>
                                    <p className="text-sm text-gray-600">{formatDate(event.eventDate)}</p>
                                    {event.startTime && (
                                        <p className="text-sm text-gray-500">{event.startTime}</p>
                                    )}
                                    <p className="text-sm text-cabaza-purple mt-1">{event.placeName}</p>
                                    <p className="text-xs text-gray-500">{event.placeAddress}</p>
                                </div>
                            </Popup>
                        </Marker>
                    ))}
                </MapContainer>
            </div>

            {/* 4 Columns for event categories */}
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 min-h-0 overflow-hidden">
                {/* Column 1: Public Events */}
                <div className="flex flex-col min-h-0">
                    <div className="flex items-center gap-2 mb-2 px-1">
                        <Globe className="w-4 h-4 text-green-600" />
                        <h3 className="font-semibold text-gray-700 text-sm">
                            {language === 'fr' ? 'Publics' : 'Public'}
                        </h3>
                        <span className="text-xs bg-green-100 text-green-600 px-1.5 py-0.5 rounded-full">
                            {publicEvents.length}
                        </span>
                    </div>
                    <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                        {loading ? (
                            <div className="flex items-center justify-center h-20">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cabaza-purple"></div>
                            </div>
                        ) : publicEvents.length === 0 ? (
                            <p className="text-xs text-gray-400 text-center py-4">
                                {language === 'fr' ? 'Aucun événement' : 'No events'}
                            </p>
                        ) : (
                            publicEvents.map(event => renderEventCard(event))
                        )}
                    </div>
                </div>

                {/* Column 2: Past Events */}
                <div className="flex flex-col min-h-0">
                    <div className="flex items-center gap-2 mb-2 px-1">
                        <History className="w-4 h-4 text-gray-500" />
                        <h3 className="font-semibold text-gray-700 text-sm">
                            {language === 'fr' ? 'Passés' : 'Past'}
                        </h3>
                        <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">
                            {pastEvents.length}
                        </span>
                    </div>
                    <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                        {loading ? (
                            <div className="flex items-center justify-center h-20">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cabaza-purple"></div>
                            </div>
                        ) : pastEvents.length === 0 ? (
                            <p className="text-xs text-gray-400 text-center py-4">
                                {language === 'fr' ? 'Aucun événement' : 'No events'}
                            </p>
                        ) : (
                            pastEvents.map(event => renderEventCard(event))
                        )}
                    </div>
                </div>

                {/* Column 3: Invitations */}
                <div className="flex flex-col min-h-0">
                    <div className="flex items-center gap-2 mb-2 px-1">
                        <UserPlus className="w-4 h-4 text-orange-500" />
                        <h3 className="font-semibold text-gray-700 text-sm">
                            {language === 'fr' ? 'Invitations' : 'Invitations'}
                        </h3>
                        <span className="text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded-full">
                            {invitedEvents.length}
                        </span>
                    </div>
                    <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                        {loading ? (
                            <div className="flex items-center justify-center h-20">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cabaza-purple"></div>
                            </div>
                        ) : invitedEvents.length === 0 ? (
                            <p className="text-xs text-gray-400 text-center py-4">
                                {language === 'fr' ? 'Aucune invitation' : 'No invitations'}
                            </p>
                        ) : (
                            invitedEvents.map(event => renderEventCard(event))
                        )}
                    </div>
                </div>

                {/* Column 4: My Events */}
                <div className="flex flex-col min-h-0">
                    <div className="flex items-center gap-2 mb-2 px-1">
                        <Calendar className="w-4 h-4 text-cabaza-purple" />
                        <h3 className="font-semibold text-gray-700 text-sm">
                            {language === 'fr' ? 'Mes événements' : 'My Events'}
                        </h3>
                        <span className="text-xs bg-cabaza-purple/20 text-cabaza-purple px-1.5 py-0.5 rounded-full">
                            {myEvents.length}
                        </span>
                    </div>
                    <div className="flex-1 overflow-y-auto space-y-2 pr-1">
                        {loading ? (
                            <div className="flex items-center justify-center h-20">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cabaza-purple"></div>
                            </div>
                        ) : myEvents.length === 0 ? (
                            <p className="text-xs text-gray-400 text-center py-4">
                                {language === 'fr' ? 'Aucun événement' : 'No events'}
                            </p>
                        ) : (
                            myEvents.map(event => renderEventCard(event))
                        )}
                    </div>
                </div>
            </div>

            {/* Create Event Modal */}
            {showCreateModal && (
                <CreateEventModal
                    onClose={() => setShowCreateModal(false)}
                    onEventCreated={() => {
                        setShowCreateModal(false);
                        loadAllEvents();
                    }}
                    currentUserId={currentUserId}
                    language={language}
                />
            )}

            {/* Edit Event Modal */}
            {editingEvent && (
                <EditEventModal
                    event={editingEvent}
                    onClose={() => setEditingEvent(null)}
                    onEventUpdated={() => {
                        setEditingEvent(null);
                        loadAllEvents();
                    }}
                    currentUserId={currentUserId}
                    language={language}
                />
            )}

            {/* Participants Modal */}
            <Modal
                isOpen={showParticipants && !!selectedEvent}
                onClose={() => setShowParticipants(false)}
                title={language === 'fr' ? 'Participants' : 'Participants'}
                icon={Users}
                size="sm"
                hideFooter
            >
                {selectedEvent && (
                    <>
                        <h3 className="text-lg font-medium text-cabaza-purple mb-4">{selectedEvent.title}</h3>

                        {participants.length === 0 ? (
                            <p className="text-gray-500 text-center py-4">
                                {language === 'fr' ? 'Aucun participant pour le moment' : 'No participants yet'}
                            </p>
                        ) : (
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                {participants.map(p => (
                                    <div key={p.id} className="flex items-center justify-between gap-3 p-2 bg-gray-50 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-cabaza-purple/20 rounded-full flex items-center justify-center">
                                                <User className="w-4 h-4 text-cabaza-purple" />
                                            </div>
                                            <span className="font-medium text-gray-800">
                                                {p.firstName} {p.lastName}
                                            </span>
                                        </div>
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                                            p.status === 'confirmed' ? 'bg-green-100 text-green-600' :
                                            p.status === 'invited' ? 'bg-orange-100 text-orange-600' :
                                            p.status === 'declined' ? 'bg-red-100 text-red-600' :
                                            'bg-gray-100 text-gray-600'
                                        }`}>
                                            {p.status === 'confirmed' && (language === 'fr' ? 'Confirmé' : 'Confirmed')}
                                            {p.status === 'invited' && (language === 'fr' ? 'Invité' : 'Invited')}
                                            {p.status === 'declined' && (language === 'fr' ? 'Décliné' : 'Declined')}
                                            {p.status === 'maybe' && (language === 'fr' ? 'Peut-être' : 'Maybe')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </Modal>

            {/* Event Detail Modal */}
            <Modal
                isOpen={!!selectedEvent && !showParticipants}
                onClose={() => setSelectedEvent(null)}
                title={selectedEvent?.title || ''}
                icon={Calendar}
                size="md"
                hideFooter
            >
                {selectedEvent && (
                    <div className="space-y-4">
                        {/* Event info */}
                        <div className="space-y-2">
                            <div className="flex items-center gap-2 text-gray-600">
                                <Calendar className="w-5 h-5 text-cabaza-purple" />
                                <span>{new Date(selectedEvent.eventDate).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                            </div>
                            {selectedEvent.startTime && (
                                <div className="flex items-center gap-2 text-gray-600">
                                    <Clock className="w-5 h-5 text-cabaza-purple" />
                                    <span>{selectedEvent.startTime}{selectedEvent.endTime ? ` - ${selectedEvent.endTime}` : ''}</span>
                                </div>
                            )}
                            <div className="flex items-center gap-2 text-gray-600">
                                <MapPin className="w-5 h-5 text-cabaza-purple" />
                                <div>
                                    <span>{selectedEvent.placeName}</span>
                                    <p className="text-xs text-gray-400">{selectedEvent.placeAddress}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 text-gray-500">
                                <User className="w-5 h-5 text-gray-400" />
                                <span>{selectedEvent.creatorFirstName} {selectedEvent.creatorLastName}</span>
                            </div>
                        </div>

                        {selectedEvent.description && (
                            <p className="text-gray-600 bg-gray-50 p-3 rounded-lg">{selectedEvent.description}</p>
                        )}

                        {/* Actions */}
                        <div className="flex items-center justify-between pt-4 border-t">
                            <button
                                onClick={() => {
                                    handleShowParticipants(selectedEvent);
                                }}
                                className="flex items-center gap-2 text-gray-600 hover:text-cabaza-purple transition-colors"
                            >
                                <Users className="w-5 h-5" />
                                <span>{selectedEvent.participantCount} {language === 'fr' ? 'participant(s)' : 'participant(s)'}</span>
                            </button>

                            <div className="flex gap-2">
                                {isCreator(selectedEvent) && (
                                    <Button
                                        variant="ghost"
                                        icon={Pencil}
                                        onClick={() => {
                                            setEditingEvent(selectedEvent);
                                            setSelectedEvent(null);
                                        }}
                                    >
                                        {language === 'fr' ? 'Modifier' : 'Edit'}
                                    </Button>
                                )}

                                {!isCreator(selectedEvent) && (
                                    selectedEvent.isParticipating || selectedEvent.myParticipationStatus === 'confirmed' ? (
                                        <Button
                                            variant="ghost"
                                            onClick={() => {
                                                handleLeaveEvent(selectedEvent);
                                                setSelectedEvent(null);
                                            }}
                                        >
                                            <Check className="w-4 h-4 mr-1" />
                                            {language === 'fr' ? 'Inscrit' : 'Joined'}
                                        </Button>
                                    ) : selectedEvent.myParticipationStatus === 'invited' ? (
                                        <Button
                                            onClick={() => {
                                                handleJoinEvent(selectedEvent);
                                                setSelectedEvent(null);
                                            }}
                                        >
                                            <Check className="w-4 h-4 mr-1" />
                                            {language === 'fr' ? 'Accepter' : 'Accept'}
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={() => {
                                                handleJoinEvent(selectedEvent);
                                                setSelectedEvent(null);
                                            }}
                                        >
                                            <Plus className="w-4 h-4 mr-1" />
                                            {language === 'fr' ? 'Participer' : 'Join'}
                                        </Button>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

/**
 * Create Event Modal Component
 */
const CreateEventModal = ({ onClose, onEventCreated, currentUserId, language }) => {
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        eventDate: '',
        startTime: '',
        endTime: '',
        placeName: '',
        placeAddress: '',
        latitude: null,
        longitude: null,
        country: '',
        countryCode: '',
        maxParticipants: 0,
        isPublic: true
    });
    const [saving, setSaving] = useState(false);
    const [contacts, setContacts] = useState([]);
    const [selectedContacts, setSelectedContacts] = useState([]);
    const [loadingContacts, setLoadingContacts] = useState(false);

    // Load contacts when switching to private
    useEffect(() => {
        if (!formData.isPublic && contacts.length === 0) {
            loadContacts();
        }
    }, [formData.isPublic]);

    const loadContacts = async () => {
        setLoadingContacts(true);
        try {
            // Use getVisibleUserContacts to exclude contacts with private profiles
            const userContacts = await getVisibleUserContacts();
            // Filter only contacts with a peopleId (linked to Cabaza profiles)
            const cabazaContacts = userContacts.filter(c => c.peopleid || c.peopleId);
            setContacts(cabazaContacts);
        } catch (error) {
            console.error('Error loading contacts:', error);
        }
        setLoadingContacts(false);
    };

    const toggleContact = (contact) => {
        const contactId = contact.peopleid || contact.peopleId;
        if (selectedContacts.includes(contactId)) {
            setSelectedContacts(prev => prev.filter(id => id !== contactId));
        } else {
            setSelectedContacts(prev => [...prev, contactId]);
        }
    };

    // Handle address change from AddressAutocomplete
    const handleAddressChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            placeAddress: value,
            placeName: value
        }));
    };

    // Handle coordinates from AddressAutocomplete
    const handleCoordinatesChange = (lat, lng) => {
        setFormData(prev => ({
            ...prev,
            latitude: lat,
            longitude: lng
        }));
    };

    // Handle address components from AddressAutocomplete
    const handleAddressComponentsChange = (components) => {
        setFormData(prev => ({
            ...prev,
            country: components.country || '',
            countryCode: components.countryCode || ''
        }));
    };

    const handleSubmit = async () => {
        if (!formData.title || !formData.eventDate) return;

        setSaving(true);

        // Create event with isPublic flag
        const result = await createEvent({
            ...formData,
            isPublic: formData.isPublic
        }, currentUserId);

        if (result.success) {
            // If private event and contacts selected, invite them
            if (!formData.isPublic && selectedContacts.length > 0 && result.eventId) {
                // Add each selected contact as invited participant
                for (const peopleId of selectedContacts) {
                    try {
                        await addEventParticipant(result.eventId, peopleId, 'invited');
                    } catch (err) {
                        console.error('Error inviting contact:', err);
                    }
                }
            }
            onEventCreated();
        } else {
            alert(result.message || 'Error creating event');
        }
        setSaving(false);
    };

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={language === 'fr' ? 'Créer un événement' : 'Create Event'}
            icon={Plus}
            size="lg"
            submitText={language === 'fr' ? 'Créer l\'événement' : 'Create Event'}
            cancelText={language === 'fr' ? 'Annuler' : 'Cancel'}
            onSubmit={handleSubmit}
            loading={saving}
        >
            <div className="space-y-4">
                {/* Public/Private Toggle */}
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, isPublic: true }))}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-colors ${
                            formData.isPublic
                                ? 'bg-green-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                        <Globe className="w-5 h-5" />
                        {language === 'fr' ? 'Public' : 'Public'}
                    </button>
                    <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, isPublic: false }))}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-colors ${
                            !formData.isPublic
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                        <Lock className="w-5 h-5" />
                        {language === 'fr' ? 'Privé' : 'Private'}
                    </button>
                </div>

                {formData.isPublic ? (
                    <p className="text-sm text-gray-500 bg-green-50 p-2 rounded-lg">
                        {language === 'fr'
                            ? 'Visible par tous les membres Cabaza'
                            : 'Visible to all Cabaza members'}
                    </p>
                ) : (
                    <p className="text-sm text-gray-500 bg-orange-50 p-2 rounded-lg">
                        {language === 'fr'
                            ? 'Visible uniquement par les contacts invités'
                            : 'Visible only to invited contacts'}
                    </p>
                )}

                {/* Title */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === 'fr' ? 'Titre *' : 'Title *'}
                    </label>
                    <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                    />
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === 'fr' ? 'Description' : 'Description'}
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent resize-none"
                    />
                </div>

                {/* Date */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === 'fr' ? 'Date *' : 'Date *'}
                    </label>
                    <input
                        type="date"
                        value={formData.eventDate}
                        onChange={(e) => setFormData(prev => ({ ...prev, eventDate: e.target.value }))}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                    />
                </div>

                {/* Time */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {language === 'fr' ? 'Heure de début' : 'Start Time'}
                        </label>
                        <input
                            type="time"
                            value={formData.startTime}
                            onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {language === 'fr' ? 'Heure de fin' : 'End Time'}
                        </label>
                        <input
                            type="time"
                            value={formData.endTime}
                            onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                        />
                    </div>
                </div>

                {/* Place Search using AddressAutocomplete */}
                <AddressAutocomplete
                    label={language === 'fr' ? 'Lieu (rechercher un établissement ou une adresse)' : 'Place (search for a venue or address)'}
                    value={formData.placeAddress}
                    onChange={handleAddressChange}
                    onCoordinatesChange={handleCoordinatesChange}
                    onAddressComponentsChange={handleAddressComponentsChange}
                />

                {/* Selected Place Info */}
                {formData.placeAddress && formData.latitude && (
                    <div className="bg-cabaza-purple/5 border border-cabaza-purple/20 rounded-lg p-3">
                        <p className="text-sm text-gray-600">{formData.placeAddress}</p>
                        {formData.country && (
                            <p className="text-xs text-gray-500 mt-1">{formData.country}</p>
                        )}
                    </div>
                )}

                {/* Max Participants */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === 'fr' ? 'Nombre maximum de participants (0 = illimité)' : 'Max participants (0 = unlimited)'}
                    </label>
                    <input
                        type="number"
                        min="0"
                        value={formData.maxParticipants}
                        onChange={(e) => setFormData(prev => ({ ...prev, maxParticipants: parseInt(e.target.value) || 0 }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                    />
                </div>

                {/* Invite Contacts (only for private events) */}
                {!formData.isPublic && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            <div className="flex items-center gap-2">
                                <UserPlus className="w-4 h-4" />
                                {language === 'fr' ? 'Inviter des contacts' : 'Invite contacts'}
                            </div>
                        </label>

                        {loadingContacts ? (
                            <div className="flex items-center justify-center py-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cabaza-purple"></div>
                            </div>
                        ) : contacts.length === 0 ? (
                            <p className="text-sm text-gray-500 bg-gray-50 p-3 rounded-lg">
                                {language === 'fr'
                                    ? 'Aucun contact Cabaza à inviter. Seuls les contacts liés à un profil Cabaza peuvent être invités.'
                                    : 'No Cabaza contacts to invite. Only contacts linked to a Cabaza profile can be invited.'}
                            </p>
                        ) : (
                            <div className="max-h-40 overflow-y-auto space-y-2 border border-gray-200 rounded-lg p-2">
                                {contacts.map(contact => {
                                    const contactId = contact.peopleid || contact.peopleId;
                                    const isSelected = selectedContacts.includes(contactId);
                                    return (
                                        <button
                                            key={contact.localuuid || contactId}
                                            type="button"
                                            onClick={() => toggleContact(contact)}
                                            className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${
                                                isSelected
                                                    ? 'bg-cabaza-purple/10 border border-cabaza-purple/30'
                                                    : 'bg-gray-50 hover:bg-gray-100'
                                            }`}
                                        >
                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                                isSelected ? 'bg-cabaza-purple text-white' : 'bg-gray-200'
                                            }`}>
                                                {isSelected ? <Check className="w-4 h-4" /> : <User className="w-3 h-3" />}
                                            </div>
                                            <span className="text-sm font-medium text-gray-700">
                                                {contact.firstname || contact.firstName} {contact.lastname || contact.lastName}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {selectedContacts.length > 0 && (
                            <p className="text-xs text-cabaza-purple mt-2">
                                {selectedContacts.length} {language === 'fr' ? 'contact(s) sélectionné(s)' : 'contact(s) selected'}
                            </p>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};

/**
 * Edit Event Modal Component
 */
const EditEventModal = ({ event, onClose, onEventUpdated, currentUserId, language }) => {
    const [formData, setFormData] = useState({
        title: event.title || '',
        description: event.description || '',
        eventDate: event.eventDate || '',
        startTime: event.startTime || '',
        endTime: event.endTime || '',
        placeName: event.placeName || '',
        placeAddress: event.placeAddress || '',
        latitude: event.latitude || null,
        longitude: event.longitude || null,
        country: event.country || '',
        countryCode: event.countryCode || '',
        maxParticipants: event.maxParticipants || 0,
        isPublic: event.isPublic !== false // Default to true if undefined
    });
    const [saving, setSaving] = useState(false);

    // Handle address change from AddressAutocomplete
    const handleAddressChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            placeAddress: value,
            placeName: value
        }));
    };

    // Handle coordinates from AddressAutocomplete
    const handleCoordinatesChange = (lat, lng) => {
        setFormData(prev => ({
            ...prev,
            latitude: lat,
            longitude: lng
        }));
    };

    // Handle address components from AddressAutocomplete
    const handleAddressComponentsChange = (components) => {
        setFormData(prev => ({
            ...prev,
            country: components.country || '',
            countryCode: components.countryCode || ''
        }));
    };

    const handleSubmit = async () => {
        if (!formData.title || !formData.eventDate) return;

        setSaving(true);

        const result = await updateEvent(event.eventUuid, {
            ...formData,
            creatorId: currentUserId
        });

        if (result.success) {
            onEventUpdated();
        } else {
            alert(result.message || 'Error updating event');
        }
        setSaving(false);
    };

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title={language === 'fr' ? 'Modifier l\'événement' : 'Edit Event'}
            icon={Pencil}
            size="lg"
            submitText={language === 'fr' ? 'Enregistrer' : 'Save'}
            cancelText={language === 'fr' ? 'Annuler' : 'Cancel'}
            onSubmit={handleSubmit}
            loading={saving}
        >
            <div className="space-y-4">
                {/* Public/Private Toggle */}
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, isPublic: true }))}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-colors ${
                            formData.isPublic
                                ? 'bg-green-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                        <Globe className="w-5 h-5" />
                        {language === 'fr' ? 'Public' : 'Public'}
                    </button>
                    <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, isPublic: false }))}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-colors ${
                            !formData.isPublic
                                ? 'bg-orange-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                    >
                        <Lock className="w-5 h-5" />
                        {language === 'fr' ? 'Privé' : 'Private'}
                    </button>
                </div>

                {/* Title */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === 'fr' ? 'Titre *' : 'Title *'}
                    </label>
                    <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                    />
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === 'fr' ? 'Description' : 'Description'}
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent resize-none"
                    />
                </div>

                {/* Date */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === 'fr' ? 'Date *' : 'Date *'}
                    </label>
                    <input
                        type="date"
                        value={formData.eventDate}
                        onChange={(e) => setFormData(prev => ({ ...prev, eventDate: e.target.value }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                    />
                </div>

                {/* Time */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {language === 'fr' ? 'Heure de début' : 'Start Time'}
                        </label>
                        <input
                            type="time"
                            value={formData.startTime}
                            onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {language === 'fr' ? 'Heure de fin' : 'End Time'}
                        </label>
                        <input
                            type="time"
                            value={formData.endTime}
                            onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                        />
                    </div>
                </div>

                {/* Place Search using AddressAutocomplete */}
                <AddressAutocomplete
                    label={language === 'fr' ? 'Lieu (rechercher un établissement ou une adresse)' : 'Place (search for a venue or address)'}
                    value={formData.placeAddress}
                    onChange={handleAddressChange}
                    onCoordinatesChange={handleCoordinatesChange}
                    onAddressComponentsChange={handleAddressComponentsChange}
                />

                {/* Selected Place Info */}
                {formData.placeAddress && formData.latitude && (
                    <div className="bg-cabaza-purple/5 border border-cabaza-purple/20 rounded-lg p-3">
                        <p className="text-sm text-gray-600">{formData.placeAddress}</p>
                        {formData.country && (
                            <p className="text-xs text-gray-500 mt-1">{formData.country}</p>
                        )}
                    </div>
                )}

                {/* Max Participants */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {language === 'fr' ? 'Nombre maximum de participants (0 = illimité)' : 'Max participants (0 = unlimited)'}
                    </label>
                    <input
                        type="number"
                        min="0"
                        value={formData.maxParticipants}
                        onChange={(e) => setFormData(prev => ({ ...prev, maxParticipants: parseInt(e.target.value) || 0 }))}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                    />
                </div>
            </div>
        </Modal>
    );
};

export default Events;
